/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The XML-feed portals (ZAP, Viva Real, Chaves na Mão) as network rows, the
 * same row the Facebook & Instagram card uses: brand badge, title, state
 * line, and either a connect button or the sending switch.
 */

import { Button, Text } from '@/shared/ui';
import { formatLocalizedDateTime } from '@/shared/utils/date';
import { NetworkRow, Toggle } from '../FacebookSettingsSection/FacebookSettingsParts';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Brand colour and short mark per feed portal. */
const FEED_BRANDS: Record<string, { color: string; mark: string }> = {
  zap: { color: '#F25C05', mark: 'ZAP' },
  vivareal: { color: '#1C64F2', mark: 'VR' },
  chavesnamao: { color: '#E30613', mark: 'CM' },
};

export interface FeedPortalRow {
  id: string;
  label: string;
  description: string;
  connected: boolean;
  enabled: boolean;
  autoPublish: boolean;
  lastReadAt?: string;
}

export interface FeedPortalsBlockProps {
  portals: FeedPortalRow[];
  autoPublishActive: boolean;
  saving: boolean;
  onConnect: (portalId: string) => void;
  onToggleEnabled: (portalId: string) => void;
  onToggleAutoPublish: (portalId: string) => void;
  t: Translate;
}

/** The feed portal's round brand mark, falling back to its initials. */
function FeedBadge({ portalId }: { portalId: string }) {
  const brand = FEED_BRANDS[portalId] ?? { color: 'var(--text-tertiary)', mark: portalId.slice(0, 2).toUpperCase() };
  return (
    <Text
      component="span"
      aria-hidden="true"
      className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white text-xs font-extrabold shrink-0"
      style={{ background: brand.color }}
    >
      {brand.mark}
    </Text>
  );
}

/** The feed portal's status line: connection state and, when connected, the last read. */
function identityLine(portal: FeedPortalRow, t: Translate): string {
  if (!portal.connected) return `${t('portal.naoConectado')} · ${portal.description}`;
  const read = portal.lastReadAt
    ? ` · ${t('portal.ultimaLeitura', { date: formatLocalizedDateTime(portal.lastReadAt) })}`
    : '';
  return `${t('portal.conectado')}${read}`;
}

/**
 * Card listing the feed portals.
 *
 * @param props - Rows, switch states and actions.
 * @returns The card body.
 */
export function FeedPortalsBlock({
  portals, autoPublishActive, saving, onConnect, onToggleEnabled, onToggleAutoPublish, t,
}: FeedPortalsBlockProps) {
  return (
    <div className="flex flex-col gap-(--space-4)" data-testid="feed-portals">
      {portals.map((portal) => (
        <div key={portal.id} className="flex flex-col gap-(--space-3)">
          <NetworkRow
            testId={`network-row-${portal.id}`}
            icon={<FeedBadge portalId={portal.id} />}
            title={portal.label}
            identity={identityLine(portal, t)}
            trailing={portal.connected ? (
              <Toggle
                checked={portal.enabled}
                testId={`toggle-${portal.id}`}
                disabled={saving}
                onClick={() => onToggleEnabled(portal.id)}
              />
            ) : (
              <Button variant="outlined" size="xs" data-testid={`portal-connect-${portal.id}`} onClick={() => onConnect(portal.id)}>
                {t('portal.conectarBtn')}
              </Button>
            )}
          />
          {portal.connected && portal.enabled && autoPublishActive && (
            <div className="flex items-start gap-(--space-3) pl-(--space-8)">
              <Toggle
                checked={portal.autoPublish}
                size={16}
                testId={`toggle-${portal.id}-autopublish`}
                disabled={saving}
                onClick={() => onToggleAutoPublish(portal.id)}
              />
              <div className="flex-1">
                <Text component="span" className="text-sm font-medium text-(--text-primary)">{t('portal.publicacaoAutomatica')}</Text>
                <div className="text-sm text-(--text-secondary) mt-(--space-1) leading-relaxed">
                  {portal.autoPublish ? t('v2.portals.autoPublishOn') : t('v2.portals.autoPublishOff')}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
